import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { publicApi, useMicrositeContext } from "./MicrositeLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft, Calendar as CalIcon, User } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function MicrositeBook() {
  const { clinic, slug, basePath } = useMicrositeContext();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const initialDoctor = search.get("doctor") || "";

  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(initialDoctor);
  const [date, setDate] = useState(today());
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [picked, setPicked] = useState(""); // "HH:MM"
  const [form, setForm] = useState({ patient_name: "", phone: "", reason: "", age: "", gender: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const primary = clinic.primary_color || "#2563EB";
  const blocked = clinic.status && clinic.status !== "open";

  useEffect(() => {
    publicApi.get(`/public/clinic/${slug}/doctors`).then(({ data }) => {
      setDoctors(data);
      if (!doctorId && data[0]) setDoctorId(data[0].user_id);
    });
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    if (!doctorId || !date) return;
    setLoadingSlots(true);
    setPicked("");
    publicApi.get(`/public/clinic/${slug}/slots`, { params: { doctor_id: doctorId, date } })
      .then(({ data }) => setSlots(data))
      .catch(() => setSlots(null))
      .finally(() => setLoadingSlots(false));
  }, [doctorId, date, slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!picked) { setError("Pick a time slot"); return; }
    if (!form.patient_name.trim() || !form.phone.trim()) {
      setError("Name and phone are required"); return;
    }
    setSubmitting(true);
    try {
      const dt = new Date(`${date}T${picked}:00`);
      const { data } = await publicApi.post(`/public/clinic/${slug}/book`, {
        patient_name: form.patient_name.trim(),
        phone: form.phone.trim(),
        doctor_id: doctorId,
        date_time: dt.toISOString(),
        reason: form.reason.trim(),
        age: form.age ? parseInt(form.age, 10) : null,
        gender: form.gender || null,
      });
      setResult(data);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Success view
  if (result) {
    const apt = result.appointment;
    const fmt = new Date(apt.date_time).toLocaleString("en-IN", {
      weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hour12: true,
    });
    return (
      <div className="max-w-xl mx-auto" data-testid="booking-success">
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-8 sm:p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Booking confirmed</h2>
            <p className="text-slate-600 mb-6">
              We&apos;ve reserved your slot. {result.sms_sent ? "A confirmation SMS has been sent." : "Please save these details — SMS confirmation is unavailable right now."}
            </p>
            <div className="bg-white rounded-xl border border-emerald-200 p-5 text-left space-y-2">
              <Row label="Patient" value={apt.patient_name} />
              <Row label="Doctor" value={apt.doctor_name} />
              <Row label="When" value={fmt} />
              {apt.reason && <Row label="Reason" value={apt.reason} />}
              <Row label="Booking ID" value={apt.appointment_id.slice(-8).toUpperCase()} mono />
            </div>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate(basePath || "/")}>Back to home</Button>
              <Button onClick={() => { setResult(null); setPicked(""); }} style={{ backgroundColor: primary, color: "white" }}>
                Book another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="microsite-book">
      <button onClick={() => navigate(basePath || "/")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>Book an appointment</h1>
      <p className="text-slate-500 mb-8">Pick a doctor and time — we&apos;ll send you an SMS confirmation.</p>

      {blocked && (
        <Card className="border-amber-200 bg-amber-50/40 mb-6" data-testid="booking-blocked">
          <CardContent className="p-5 flex items-start gap-3 text-amber-800">
            <AlertCircle className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-semibold">Online booking is paused</div>
              <div className="text-sm mt-0.5">{clinic.status_message || `Clinic is ${clinic.status}.`}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Doctor</Label>
                <Select value={doctorId} onValueChange={setDoctorId} disabled={blocked}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="book-doctor-select"><SelectValue placeholder="Pick doctor" /></SelectTrigger>
                  <SelectContent>
                    {doctors.map((d) => (<SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date" value={date} min={today()}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={blocked}
                  className="mt-1.5 h-11"
                  data-testid="book-date-input"
                />
              </div>
            </div>
            <div>
              <Label>Pick a time slot</Label>
              <div className="mt-2">
                {loadingSlots ? (
                  <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
                ) : !slots ? null : slots.on_leave ? (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    {slots.closed ? `Clinic is ${slots.status}. Booking is closed.` : "Doctor is on leave this day."}
                  </div>
                ) : slots.slots.length === 0 ? (
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 text-sm">No working hours configured for this day.</div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-auto" data-testid="public-slot-grid">
                    {slots.slots.map((s) => {
                      const selected = picked === s.time;
                      if (!s.available) {
                        return (
                          <button key={s.time} type="button" disabled
                            className="px-2 py-2 text-sm rounded-md bg-slate-100 border border-slate-200 text-slate-400 line-through cursor-not-allowed"
                            data-testid={`slot-${s.time}`}
                          >{s.time}</button>
                        );
                      }
                      return (
                        <button
                          key={s.time} type="button"
                          onClick={() => setPicked(s.time)}
                          className={`px-2 py-2 text-sm rounded-md border transition-all ${selected ? "text-white font-semibold" : "bg-white border-slate-200 hover:border-slate-300 text-slate-700"}`}
                          style={selected ? { backgroundColor: primary, borderColor: primary } : {}}
                          data-testid={`slot-${s.time}`}
                        >
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" /> Your details
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bk-name">Full name</Label>
                <Input id="bk-name" required maxLength={80} value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1.5 h-11" data-testid="book-name-input" />
              </div>
              <div>
                <Label htmlFor="bk-phone">Mobile number</Label>
                <Input id="bk-phone" required type="tel" inputMode="numeric" maxLength={14} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" className="mt-1.5 h-11" data-testid="book-phone-input" />
              </div>
              <div>
                <Label htmlFor="bk-age">Age (optional)</Label>
                <Input id="bk-age" type="number" min="0" max="130" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 h-11" data-testid="book-age-input" />
              </div>
              <div>
                <Label>Gender (optional)</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="book-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="bk-reason">Reason for visit (optional)</Label>
              <Input id="bk-reason" maxLength={200} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Fever and cough" className="mt-1.5 h-11" data-testid="book-reason-input" />
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="booking-error">{error}</div>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={blocked || submitting || !picked}
            style={{ backgroundColor: primary, color: "white" }}
            className="h-11 px-6 hover:opacity-90"
            data-testid="book-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalIcon className="w-4 h-4 mr-2" />}
            Confirm booking
          </Button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
      <span className={`text-slate-900 text-sm ${mono ? "font-mono" : ""} text-right`}>{value}</span>
    </div>
  );
}
